import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Plus, Trash2, AlertCircle, CheckCircle2, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { EMISSION_CATEGORIES, COUNTRIES, UNITS, getUnitCategory, getScopeFromCategory, getCategoryLabel, ACTIVITY_CLASSIFICATION_OPTIONS } from '../lib/constants';
import { useCategoryRules } from '../lib/categoryRules';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Column definitions for the spreadsheet
const COLUMNS = [
  { key: 'category', label: 'Category', type: 'select', required: true, width: 180, options: 'categories' },
  { key: 'activity_classification', label: 'Activity Classification', type: 'select', required: true, width: 160, options: 'activity_classifications' },
  { key: 'sub_category', label: 'Sub Category', type: 'text', required: true, width: 140 },
  { key: 'type', label: 'Type', type: 'text', required: false, width: 120 },
  { key: 'sub_type', label: 'Sub Type', type: 'text', required: false, width: 120 },
  { key: 'continent', label: 'Continent', type: 'select', required: false, width: 110, options: 'continents' },
  { key: 'country', label: 'Country', type: 'select', required: true, width: 140, options: 'countries' },
  { key: 'state', label: 'State', type: 'text', required: false, width: 100 },
  { key: 'city', label: 'City', type: 'text', required: false, width: 100 },
  { key: 'input_unit', label: 'Input Unit', type: 'select', required: true, width: 100, options: 'units' },
  { key: 'co2e', label: 'CO2e', type: 'number', required: false, width: 100 },
  { key: 'co2factor', label: 'CO2 Factor', type: 'number', required: false, width: 100 },
  { key: 'ch4factor', label: 'CH4 Factor', type: 'number', required: false, width: 100 },
  { key: 'n2o_factor', label: 'N2O Factor', type: 'number', required: false, width: 100 },
  { key: 'no2factor', label: 'NO2 Factor', type: 'number', required: false, width: 100 },
  { key: 'co4factor', label: 'CO4 Factor', type: 'number', required: false, width: 100 },
  { key: 'biogenic_co2factor', label: 'Biogenic CO2', type: 'number', required: false, width: 110 },
  { key: 'source_name', label: 'Source Name', type: 'text', required: true, width: 140 },
  { key: 'source_version', label: 'Source Version', type: 'text', required: true, width: 110 },
  { key: 'sourceurl', label: 'Source URL', type: 'text', required: true, width: 200 },
  { key: 'ar_version', label: 'AR Version', type: 'text', required: false, width: 100 },
  { key: 'scope', label: 'Scope', type: 'select', required: false, width: 80, options: 'scopes' },
  { key: 'is_primary_factor', label: 'Primary Factor', type: 'boolean', required: false, width: 100 },
  { key: 'is_spend_based', label: 'Spend Based', type: 'boolean', required: false, width: 100 },
  { key: 'is_plastic', label: 'Is Plastic', type: 'select', required: false, width: 90, options: 'yesno' },
  { key: 'plastic_category', label: 'Plastic Category', type: 'text', required: false, width: 120 },
  { key: 'water_use_m3', label: 'Water Use (m³)', type: 'number', required: false, width: 110 },
  { key: 'water_data_source_name', label: 'Water Source', type: 'text', required: false, width: 120 },
  { key: 'water_data_source_version', label: 'Water Version', type: 'text', required: false, width: 100 },
  { key: 'water_data_sourceurl', label: 'Water URL', type: 'text', required: false, width: 150 },
  { key: 'flag_co2e', label: 'Flag CO2e', type: 'number', required: false, width: 100 },
  { key: 'flag_source_name', label: 'Flag Source', type: 'text', required: false, width: 120 },
  { key: 'flag_source_version', label: 'Flag Version', type: 'text', required: false, width: 100 },
  { key: 'flag_sourceurl', label: 'Flag URL', type: 'text', required: false, width: 150 },
  { key: 'site_identity', label: 'Site Identity', type: 'text', required: false, width: 120 },
  { key: 'tag', label: 'Tag', type: 'text', required: false, width: 100 },
  { key: 'description', label: 'Description', type: 'text', required: false, width: 150 },
  { key: 'additional_information', label: 'Additional Info', type: 'text', required: false, width: 150 },
];

// Create empty row
const createEmptyRow = () => {
  const row = { _id: Date.now() + Math.random(), _errors: {}, _status: 'empty' };
  COLUMNS.forEach(col => {
    row[col.key] = col.type === 'boolean' ? false : '';
  });
  return row;
};

// Validate a single row
const validateRow = (row) => {
  const errors = {};
  
  // Required field validation
  COLUMNS.forEach(col => {
    if (col.required && !row[col.key] && row[col.key] !== 0) {
      errors[col.key] = `${col.label} is required`;
    }
  });

  // Number validation
  COLUMNS.filter(c => c.type === 'number').forEach(col => {
    if (row[col.key] !== '' && row[col.key] !== null && row[col.key] !== undefined) {
      const num = Number(row[col.key]);
      if (isNaN(num)) {
        errors[col.key] = 'Must be a number';
      } else if (num < 0) {
        errors[col.key] = 'Cannot be negative';
      }
    }
  });

  // Fiber subcategory validation
  if (row.sub_category && row.sub_category.toLowerCase() === 'fiber') {
    if (!row.water_use_m3) errors.water_use_m3 = 'Required for Fiber';
    if (!row.water_data_source_name) errors.water_data_source_name = 'Required for Fiber';
    if (!row.water_data_source_version) errors.water_data_source_version = 'Required for Fiber';
    if (!row.water_data_sourceurl) errors.water_data_sourceurl = 'Required for Fiber';
    if (!row.flag_co2e) errors.flag_co2e = 'Required for Fiber';
    if (!row.flag_source_name) errors.flag_source_name = 'Required for Fiber';
    if (!row.flag_source_version) errors.flag_source_version = 'Required for Fiber';
    if (!row.flag_sourceurl) errors.flag_sourceurl = 'Required for Fiber';
  }

  return errors;
};

// Check if row has data
const rowHasData = (row) => {
  return COLUMNS.some(col => {
    const val = row[col.key];
    return val !== '' && val !== null && val !== undefined && val !== false;
  });
};

export default function BulkEmissionFactorPage() {
  const navigate = useNavigate();
  const { continents, country_continents: countryContinents } = useCategoryRules();
  const [rows, setRows] = useState(() => Array(5).fill(null).map(createEmptyRow));
  const [submitting, setSubmitting] = useState(false);
  const [dragFillStart, setDragFillStart] = useState(null);
  const [dragFillPreview, setDragFillPreview] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [submitResults, setSubmitResults] = useState(null);
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);
  const [subCategories, setSubCategories] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  // Fetch sub-categories for autocomplete
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/emission-factors/sub-categories/list`);
        setSubCategories(response.data || []);
      } catch (error) {
        console.error('Failed to fetch sub-categories:', error);
      }
    };
    fetchSubCategories();
  }, []);

  // Update cell value
  const updateCell = useCallback((rowIndex, key, value) => {
    setRows(prev => {
      const newRows = [...prev];
      // Handle empty select value
      const actualValue = value === '__empty__' ? '' : value;
      newRows[rowIndex] = { ...newRows[rowIndex], [key]: actualValue };
      
      // Auto-set scope and unit_category based on category
      if (key === 'category' && actualValue) {
        const categoryLabel = getCategoryLabel(actualValue);
        newRows[rowIndex].scope = getScopeFromCategory(categoryLabel);
      }
      if (key === 'input_unit' && actualValue) {
        newRows[rowIndex].unit_category = getUnitCategory(actualValue);
      }
      // A country in the continent table fills in the continent; "*" or an unlisted country leaves it to be picked
      if (key === 'country') {
        newRows[rowIndex].continent = countryContinents[actualValue] || '';
      }
      
      return newRows;
    });
  }, [countryContinents]);

  // Add new rows
  const addRows = useCallback((count = 5) => {
    setRows(prev => [...prev, ...Array(count).fill(null).map(createEmptyRow)]);
  }, []);

  // Delete row
  const deleteRow = useCallback((rowIndex) => {
    setRows(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== rowIndex);
    });
  }, []);

  // Clear all rows
  const clearAll = useCallback(() => {
    setRows(Array(5).fill(null).map(createEmptyRow));
    setSubmitResults(null);
    setUploadResults(null);
  }, []);

  // Download Excel template
  const downloadTemplate = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/emission-factors/bulk-template`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'emission_factors_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  }, []);

  // Upload Excel file
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      toast.error('Please upload a .xlsx file');
      return;
    }

    setUploadingFile(true);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/emission-factors/bulk-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const results = response.data;
      setUploadResults(results);

      if (results.failed.length === 0) {
        toast.success(`Successfully uploaded ${results.success.length} emission factor(s)`);
      } else if (results.success.length > 0) {
        toast.warning(`${results.success.length} uploaded, ${results.failed.length} failed`);
      } else {
        toast.error(`All ${results.failed.length} row(s) failed. Check the errors below.`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Upload failed: ${msg}`);
      setUploadResults({ success: [], failed: [{ row: '-', errors: [msg] }], total: 0 });
    } finally {
      setUploadingFile(false);
    }
  }, []);

  // Handle paste from clipboard
  const handlePaste = useCallback(async (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text');
    
    if (!pastedText) return;

    // Parse pasted data (tab-separated for Excel)
    const pastedRows = pastedText.split('\n').filter(row => row.trim());
    const parsedData = pastedRows.map(row => row.split('\t'));

    if (parsedData.length === 0) return;

    // Find the focused cell to start pasting
    const activeElement = document.activeElement;
    const cellMatch = activeElement?.dataset?.cell?.match(/(\d+)-(\d+)/);
    
    let startRow = 0;
    let startCol = 0;
    
    if (cellMatch) {
      startRow = parseInt(cellMatch[1]);
      startCol = parseInt(cellMatch[2]);
    }

    setRows(prev => {
      const newRows = [...prev];
      
      // Add more rows if needed
      while (newRows.length < startRow + parsedData.length) {
        newRows.push(createEmptyRow());
      }

      parsedData.forEach((rowData, rowOffset) => {
        rowData.forEach((cellValue, colOffset) => {
          const targetRow = startRow + rowOffset;
          const targetCol = startCol + colOffset;
          
          if (targetCol < COLUMNS.length && targetRow < newRows.length) {
            const col = COLUMNS[targetCol];
            let value = cellValue.trim();
            
            // Convert values based on column type
            if (col.type === 'number') {
              value = value === '' ? '' : Number(value);
            } else if (col.type === 'boolean') {
              value = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
            }
            
            newRows[targetRow] = { ...newRows[targetRow], [col.key]: value };
          }
        });
      });

      return newRows;
    });

    toast.success(`Pasted ${parsedData.length} rows`);
  }, []);

  // Drag fill functionality
  const handleDragFillStart = useCallback((rowIndex, colIndex) => {
    setDragFillStart({ row: rowIndex, col: colIndex });
  }, []);

  const handleDragFillMove = useCallback((e, rowIndex) => {
    if (!dragFillStart) return;
    
    const startRow = dragFillStart.row;
    const endRow = rowIndex;
    
    if (endRow > startRow) {
      setDragFillPreview(Array.from({ length: endRow - startRow }, (_, i) => startRow + i + 1));
    } else {
      setDragFillPreview([]);
    }
  }, [dragFillStart]);

  const handleDragFillEnd = useCallback(() => {
    if (!dragFillStart || dragFillPreview.length === 0) {
      setDragFillStart(null);
      setDragFillPreview([]);
      return;
    }

    const sourceRow = rows[dragFillStart.row];
    const colKey = COLUMNS[dragFillStart.col].key;
    const sourceValue = sourceRow[colKey];

    setRows(prev => {
      const newRows = [...prev];
      dragFillPreview.forEach(rowIndex => {
        newRows[rowIndex] = { ...newRows[rowIndex], [colKey]: sourceValue };
      });
      return newRows;
    });

    toast.success(`Filled ${dragFillPreview.length} cells`);
    setDragFillStart(null);
    setDragFillPreview([]);
  }, [dragFillStart, dragFillPreview, rows]);

  // Validate all rows
  const validateAllRows = useCallback(() => {
    let hasErrors = false;
    const validatedRows = rows.map(row => {
      if (!rowHasData(row)) {
        return { ...row, _errors: {}, _status: 'empty' };
      }
      const errors = validateRow(row);
      if (Object.keys(errors).length > 0) {
        hasErrors = true;
        return { ...row, _errors: errors, _status: 'error' };
      }
      return { ...row, _errors: {}, _status: 'valid' };
    });
    setRows(validatedRows);
    return !hasErrors;
  }, [rows]);

  // Submit all valid rows
  const handleSubmit = useCallback(async () => {
    // Validate first
    const rowsWithData = rows.filter(rowHasData);
    
    if (rowsWithData.length === 0) {
      toast.error('No data to submit');
      return;
    }

    // Validate all rows
    let hasErrors = false;
    const validatedRows = rows.map(row => {
      if (!rowHasData(row)) {
        return { ...row, _errors: {}, _status: 'empty' };
      }
      const errors = validateRow(row);
      if (Object.keys(errors).length > 0) {
        hasErrors = true;
        return { ...row, _errors: errors, _status: 'error' };
      }
      return { ...row, _errors: {}, _status: 'valid' };
    });
    setRows(validatedRows);

    if (hasErrors) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setConfirmDialog(true);
  }, [rows]);

  // Actually submit to API
  const confirmSubmit = useCallback(async () => {
    setConfirmDialog(false);
    setSubmitting(true);
    
    const rowsToSubmit = rows.filter(row => rowHasData(row) && Object.keys(row._errors || {}).length === 0);
    const results = { success: [], failed: [] };

    for (let i = 0; i < rowsToSubmit.length; i++) {
      const row = rowsToSubmit[i];
      try {
        // Prepare data for API
        const data = {};
        COLUMNS.forEach(col => {
          let value = row[col.key];
          if (col.type === 'number' && value !== '' && value !== null) {
            value = Number(value);
          }
          if (value !== '' && value !== null && value !== undefined) {
            data[col.key] = value;
          }
        });

        // Set unit_category from input_unit
        if (data.input_unit) {
          data.unit_category = getUnitCategory(data.input_unit);
        }

        await axios.post(`${API_URL}/emission-factors`, data);
        results.success.push(i + 1);
        
        // Update row status
        setRows(prev => {
          const newRows = [...prev];
          const rowIndex = prev.findIndex(r => r._id === row._id);
          if (rowIndex !== -1) {
            newRows[rowIndex] = { ...newRows[rowIndex], _status: 'submitted' };
          }
          return newRows;
        });
      } catch (error) {
        results.failed.push({
          row: i + 1,
          error: error.response?.data?.detail || error.message
        });
        
        // Update row status
        setRows(prev => {
          const newRows = [...prev];
          const rowIndex = prev.findIndex(r => r._id === row._id);
          if (rowIndex !== -1) {
            newRows[rowIndex] = { 
              ...newRows[rowIndex], 
              _status: 'failed',
              _errors: { _api: error.response?.data?.detail || error.message }
            };
          }
          return newRows;
        });
      }
    }

    setSubmitting(false);
    setSubmitResults(results);

    if (results.failed.length === 0) {
      toast.success(`Successfully submitted ${results.success.length} emission factors`);
    } else if (results.success.length > 0) {
      toast.warning(`${results.success.length} submitted, ${results.failed.length} failed`);
    } else {
      toast.error('All submissions failed');
    }
  }, [rows]);

  // Render cell input based on column type
  const renderCell = (row, rowIndex, col, colIndex) => {
    const hasError = row._errors?.[col.key];
    const isDragFillTarget = dragFillPreview.includes(rowIndex) && dragFillStart?.col === colIndex;
    const isSubmitted = row._status === 'submitted';
    
    const baseClass = `w-full h-8 px-2 text-sm border-0 focus:ring-1 focus:ring-primary outline-none ${
      hasError ? 'bg-red-50' : isDragFillTarget ? 'bg-blue-50' : isSubmitted ? 'bg-green-50' : 'bg-white'
    }`;

    if (col.type === 'select') {
      let options = [];
      if (col.options === 'categories') {
        options = EMISSION_CATEGORIES.map(c => ({ value: c.value, label: c.label }));
      } else if (col.options === 'continents') {
        options = continents.map(c => ({ value: c, label: c }));
      } else if (col.options === 'countries') {
        options = COUNTRIES.map(c => ({ value: c.code, label: c.name }));
      } else if (col.options === 'units') {
        options = UNITS.map(u => ({ value: u.unit, label: u.unit }));
      } else if (col.options === 'scopes') {
        options = [{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }];
      } else if (col.options === 'yesno') {
        options = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];
      } else if (col.options === 'activity_classifications') {
        options = ACTIVITY_CLASSIFICATION_OPTIONS.map(o => ({ value: o, label: o }));
      }

      return (
        <Select
          value={row[col.key] || undefined}
          onValueChange={(value) => updateCell(rowIndex, col.key, value)}
          disabled={isSubmitted}
        >
          <SelectTrigger 
            className={`${baseClass} border`}
            data-cell={`${rowIndex}-${colIndex}`}
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="__empty__">—</SelectItem>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (col.type === 'boolean') {
      return (
        <select
          value={row[col.key] ? 'true' : 'false'}
          onChange={(e) => updateCell(rowIndex, col.key, e.target.value === 'true')}
          className={baseClass}
          data-cell={`${rowIndex}-${colIndex}`}
          disabled={isSubmitted}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      );
    }

    return (
      <input
        onWheel={(e) => e.currentTarget.blur()} // number cells must not change on scroll
        type={col.type === 'number' ? 'number' : 'text'}
        value={row[col.key] ?? ''}
        onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
        className={baseClass}
        data-cell={`${rowIndex}-${colIndex}`}
        step={col.type === 'number' ? 'any' : undefined}
        disabled={isSubmitted}
        list={col.key === 'sub_category' ? 'sub-categories-list' : undefined}
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-in" data-testid="bulk-ef-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emission-factors')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Bulk Add Emission Factors</h1>
              <p className="text-sm text-muted-foreground">Add multiple emission factors at once - Excel-like interface</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="btn-download-template">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xlsm"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="input-upload-excel"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              data-testid="btn-upload-excel"
            >
              {uploadingFile ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Excel
            </Button>
            <div className="w-px bg-gray-300 mx-1" />
            <Button variant="outline" onClick={clearAll}>
              Clear All
            </Button>
            <Button variant="outline" onClick={() => addRows(5)}>
              <Plus className="w-4 h-4 mr-2" />
              Add 5 Rows
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="btn-primary">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Submit All
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <div className="flex items-start gap-2 text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Tips:</strong> 
                <span className="ml-1">
                  <strong>Upload Excel</strong> (.xlsx) for bulk imports — download the template first for the correct format.
                  You can also copy-paste from Excel (Ctrl+V) or fill the grid manually. Required fields are marked with *.
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Results */}
        {uploadResults && (
          <Card
            className={
              uploadResults.failed.length > 0 && uploadResults.success.length === 0
                ? 'border-red-200 bg-red-50'
                : uploadResults.failed.length > 0
                ? 'border-amber-200 bg-amber-50'
                : 'border-green-200 bg-green-50'
            }
            data-testid="upload-results"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-2 text-sm">
                <FileSpreadsheet className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  uploadResults.failed.length > 0 && uploadResults.success.length === 0
                    ? 'text-red-600'
                    : uploadResults.failed.length > 0
                    ? 'text-amber-600'
                    : 'text-green-600'
                }`} />
                <div className="flex-1">
                  <div className="font-semibold">
                    Excel Upload Results — Total rows: {uploadResults.total}
                  </div>
                  <div className="mt-1 space-x-4">
                    <span className="text-green-700">
                      ✓ {uploadResults.success.length} created
                    </span>
                    {uploadResults.failed.length > 0 && (
                      <span className="text-red-600">
                        ✗ {uploadResults.failed.length} failed
                      </span>
                    )}
                  </div>
                  {uploadResults.success.length > 0 && (
                    <div className="mt-2 text-xs text-gray-700">
                      Created EF IDs:{' '}
                      {uploadResults.success.slice(0, 20).map(s => `#${s.emission_factor_id}`).join(', ')}
                      {uploadResults.success.length > 20 ? `, … (+${uploadResults.success.length - 20} more)` : ''}
                    </div>
                  )}
                  {uploadResults.failed.length > 0 && (
                    <div className="mt-3 max-h-48 overflow-y-auto bg-white rounded border border-red-200 p-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-red-100 text-red-800">
                            <th className="p-1 text-left w-20">Row</th>
                            <th className="p-1 text-left">Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResults.failed.map((f, idx) => (
                            <tr key={idx} className="border-t border-red-100">
                              <td className="p-1 font-mono">{f.row}</td>
                              <td className="p-1 text-red-700">
                                {(f.errors || []).join('; ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {uploadResults.success.length > 0 && (
                    <div className="mt-3">
                      <Button size="sm" variant="outline" onClick={() => navigate('/pending-approvals')}>
                        View in Pending Approvals →
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Results */}
        {submitResults && (
          <Card className={submitResults.failed.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
            <CardContent className="p-3">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${submitResults.failed.length > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                <div>
                  <strong>{submitResults.success.length} submitted successfully</strong>
                  {submitResults.failed.length > 0 && (
                    <span className="text-red-600 ml-2">
                      • {submitResults.failed.length} failed
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Spreadsheet */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            <div 
              className="overflow-auto max-h-[calc(100vh-300px)]"
              ref={tableRef}
              onPaste={handlePaste}
              onMouseUp={handleDragFillEnd}
              onMouseLeave={handleDragFillEnd}
            >
              {/* Sub-category datalist for autocomplete */}
              <datalist id="sub-categories-list">
                {subCategories.map(sc => (
                  <option key={sc} value={sc} />
                ))}
              </datalist>

              <table className="w-full border-collapse" style={{ minWidth: COLUMNS.reduce((sum, col) => sum + col.width, 100) }}>
                <thead className="sticky top-0 z-10 bg-gray-100">
                  <tr>
                    <th className="w-12 p-2 text-xs font-medium text-gray-600 border-b border-r bg-gray-100 sticky left-0">#</th>
                    {COLUMNS.map((col, colIndex) => (
                      <th 
                        key={col.key}
                        className="p-2 text-xs font-medium text-gray-600 border-b border-r text-left whitespace-nowrap"
                        style={{ minWidth: col.width, width: col.width }}
                      >
                        {col.label}
                        {col.required && <span className="text-red-500 ml-0.5">*</span>}
                      </th>
                    ))}
                    <th className="w-10 p-2 border-b bg-gray-100"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr 
                      key={row._id}
                      className={`
                        ${row._status === 'error' ? 'bg-red-50' : ''}
                        ${row._status === 'submitted' ? 'bg-green-50' : ''}
                        ${row._status === 'failed' ? 'bg-red-100' : ''}
                        hover:bg-gray-50
                      `}
                      onMouseMove={(e) => handleDragFillMove(e, rowIndex)}
                    >
                      <td className="p-1 text-xs text-center text-gray-500 border-b border-r bg-gray-50 sticky left-0">
                        <div className="flex items-center justify-center gap-1">
                          <span>{rowIndex + 1}</span>
                          {row._status === 'submitted' && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                          {row._status === 'error' && <AlertCircle className="w-3 h-3 text-red-500" />}
                          {row._status === 'failed' && <AlertCircle className="w-3 h-3 text-red-600" />}
                        </div>
                      </td>
                      {COLUMNS.map((col, colIndex) => (
                        <td 
                          key={col.key}
                          className="p-0 border-b border-r relative"
                          style={{ minWidth: col.width }}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="relative">
                                  {renderCell(row, rowIndex, col, colIndex)}
                                  {/* Drag fill handle */}
                                  {row[col.key] && row._status !== 'submitted' && (
                                    <div
                                      className="absolute bottom-0 right-0 w-2 h-2 bg-primary cursor-crosshair"
                                      onMouseDown={() => handleDragFillStart(rowIndex, colIndex)}
                                    />
                                  )}
                                </div>
                              </TooltipTrigger>
                              {row._errors?.[col.key] && (
                                <TooltipContent side="top" className="bg-red-600 text-white">
                                  {row._errors[col.key]}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      ))}
                      <td className="p-1 border-b">
                        {row._status !== 'submitted' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => deleteRow(rowIndex)}
                          >
                            <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {rows.length} rows • {rows.filter(rowHasData).length} with data
              </div>
              <Button variant="outline" size="sm" onClick={() => addRows(1)}>
                <Plus className="w-3 h-3 mr-1" />
                Add Row
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Confirm Dialog */}
        <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Submit Emission Factors?</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to submit {rows.filter(r => rowHasData(r) && Object.keys(r._errors || {}).length === 0).length} emission factors. 
                They will be sent for approval.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSubmit} className="btn-primary">
                Submit All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
